import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Tab, TabPane } from 'semantic-ui-react'
import { GeneralService } from '~/api/services/general'
import Layout from '~/components/Layout'
import ProfileApi from '~/api/services/profile'
import notify from '~/components/Notify'

//Tabs
import GeneralInformation from './Tabs/GeneralInformation'
import Support from './Tabs/Support'
import DocumentsComponent from './Tabs/DocumentsComponent'
import { useAuth } from '~/routes/components/AuthProvider'
import AuthService from '~/api/services/auth'
import { StyledMenuItem } from './style';
import BasePriceText from './Tabs/BasePriceText'
import { handleErrors } from '~/utils/helpers'

const General: React.FunctionComponent = () => {
  const [isBusy, setIsBusy] = useState(false)
  const { setSystemData } = useAuth()
  const [generalOptions, setGeneralOptions] = useState(null)
  const [countriesCodes, setCountriesCodes] = useState([])
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [errorTabs, setErrorTabs] = useState([])
  const [tabIndex, setTabIndex] = useState(-1)
  const { t } = useTranslation()
  const methods = useForm({
    mode: 'onSubmit'
  })

  useEffect(() => {
    fetchGeneralData()
  }, [t])

  useEffect(() => {
    const { formState } = methods
    if (Object.keys(methods.formState.errors).length > 0) {
      if (formState.errors?.support) {
        //@ts-ignore
        const tabIndexLang = formState.errors?.support ? formState.errors?.support?.languages?.findIndex(language => !!language) : -1
        if (tabIndexLang >= 0) {
          setActiveIndex(4)
          setTabIndex(tabIndexLang)
        }
      } else {
        //@ts-ignore
        const indexErrors = formState.errors?.documents?.map((document, index) => document.languages ? index : null).filter(item => item === 0 || !!item) || []
        if (indexErrors[0] >= 0) {
          setActiveIndex(indexErrors[0] + 1)
        }
      }
      notify(t('Please fill in all required fields'), 'error')
    }

  }, [methods.formState.errors, methods.formState.isSubmitted])

  function addLanguageIdForEmptyLanguages(languages, dataLanguages) {
    languages.map(language => {
      const index = dataLanguages.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        dataLanguages.push({
          id: language.id,
          order: language.order
        })
      } else {
        dataLanguages[index].order = language.order
      }
    })
    //sort languages by order
    dataLanguages?.sort((a, b) => a.order - b.order)

    //remove order from languages
    dataLanguages?.forEach(language => delete language.order)
    return dataLanguages
  }

  async function fetchGeneralData(documentDirty = false) {
    setIsBusy(true)
    try {
      const responseOptions = await GeneralService.getOptionsData()
      const responseData = await GeneralService.edit()
      const options = await ProfileApi.getProfileOptions()
      responseData.data.support = {
        ...responseData.data.support,
        languages: addLanguageIdForEmptyLanguages(responseOptions.data.languages, responseData.data.support?.languages || [])
      }
      responseData.data.texts[0].languages = addLanguageIdForEmptyLanguages(responseOptions.data.languages, responseData.data.texts[0].languages)
      const documentErrorTabs = []
      responseData.data.documents?.map((document, index) => {
        documentErrorTabs.push({
          name: `documents${document.type}`,
          index: index + 1,
          value: false
        })
        responseOptions.data.languages.map(language => {
          const index = document.languages.findIndex(languageText => languageText.id === language.id)
          if (index < 0) {
            document.languages.push({
              id: language.id,
              label: language.label
            })
          }
        })
      })
      setErrorTabs([
        ...documentErrorTabs,
        {
          name: 'support',
          index: documentErrorTabs.length + 1,
          value: false
        },
        {
          name: 'texts',
          index: documentErrorTabs.length + 2,
          value: false
        }
      ])
      setCountriesCodes(options.data.countries.map(country => ({ value: country.phoneCode, text: country.phoneCode, flag: country.iso })))
      setGeneralOptions(responseOptions.data)
      methods.reset(responseData.data)
      //REMOVE THIS
      if (documentDirty) {
        const localTheme = JSON.parse(localStorage.getItem('theme'))
        const responseTermsData = await AuthService.getSettingsData()
        localTheme.files = responseTermsData.data.files
        localStorage.setItem('theme', JSON.stringify(localTheme))
      }
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function errorTab() {
    const { formState } = methods
    const errorTabsTemp = [...errorTabs]
    //@ts-ignore
    const indexErrors = formState.errors?.documents?.map((document, index) => document.languages ? index : null).filter(item => item === 0 || !!item) || []
    errorTabsTemp.map((errorTab, index) => {
      if (errorTab.name.includes('documents')) {
        if (indexErrors.find(indexErrors => indexErrors + 1 === errorTab.index) !== undefined) {
          errorTabsTemp[index].value = true
        } else {
          errorTabsTemp[index].value = false
        }
      } else {
        errorTabsTemp[index].value = Object.keys(formState.errors).find(key => key === errorTab.name) !== undefined
      }
    })
    return errorTabsTemp
  }

  function getMenuItem(pane, index) {
    const errors = Object.keys(methods.formState.errors).length > 0 ? errorTab() : []
    const paneHasError = errors.find(pane => pane.index === index)

    return <StyledMenuItem
      key={pane.menuItem}
      className='tab-item'
    >
      <div className={`${paneHasError?.value ? 'error' : ''}`}>{pane.menuItem}</div>
    </StyledMenuItem>
  }

  function getFlagName(flag = null, language) {
    const flagName = flag || language?.code
    switch (flagName) {
      case 'en':
        return 'gb'
      case 'fr_BE':
        return 'be'
      case 'nl_BE':
        return 'be'
      default:
        return flagName
    }
  }

  function renderTabs() {
    const documents = methods.getValues('documents').map(document => {
      return {
        menuItem: document.label.toString(),
        component: <DocumentsComponent
          key={document.id}
          languages={generalOptions?.languages}
          types={generalOptions?.fileTypes.map(item => ({ value: item.key, text: item.label }))}
          type={document.type}
          label={document.label}
        />
      }
    })
    const panes = [
      {
        menuItem: t('General information'),
        component: <GeneralInformation
          languageOptions={generalOptions?.languages.map((item) => ({
            key: item.id,
            value: item.id,
            text: item.label,
            flag: getFlagName(null, item)
          }))}
        />
      },
      ...documents,
      {
        menuItem: t('Support'),
        component: <Support
          key={t('Support')}
          countriesCodes={countriesCodes}
          tabIndex={tabIndex}
          languages={generalOptions?.languages || []}
        />
      },
      {
        menuItem: t('Base price text'),
        component: <BasePriceText
          languages={generalOptions?.languages || []}
        />
      }
    ]

    return <Tab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeIndex}
      onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
      renderActiveOnly={false}
      panes={
        panes.map((pane, index) => {
          return {
            menuItem: getMenuItem(pane, index),
            pane: <TabPane
              // eslint-disable-next-line
              children={pane.component}
              active={index === activeIndex}
              style={{ border: 'none' }}
              key={pane.menuItem}
            />
          }
        })}
    />
  }

  async function onSubmit(data) {
    const textsUpdated = Object.keys(methods.formState.dirtyFields).find(key => key === 'texts')
    setIsBusy(true)
    try {
      await GeneralService.update(data)
      await fetchGeneralData(methods.formState.dirtyFields.documents)
      if (textsUpdated) {
        const response = await AuthService.getSystemData()
        setSystemData(response.data)
      }
      notify(t('General settings updated'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Layout
    onSubmit={methods.handleSubmit(onSubmit)}
    segmented
    isBusy={isBusy}
    action={t('Update')}
    title={t('General settings')}
  >
    {generalOptions && !isBusy && <FormProvider {...methods}>
      {renderTabs()}
    </FormProvider>}
  </Layout>
}
export default General
